import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import {
  ReportTable, Caveats, AdminReportGate, reportGet, formatStatus, money, Finding,
} from "@/components/reports/report-primitives";
import { CountBarChart } from "@/components/reports/report-charts";

const usd = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

export default function BillingReportPage() {
  usePageTitle("Billing");
  const [, setLocation] = useLocation();
  const ar = useQuery({ queryKey: ["/api/reports/ar-aging"], queryFn: () => reportGet("/api/reports/ar-aging") });
  const exceptions = useQuery({ queryKey: ["/api/reports/billing-exceptions"], queryFn: () => reportGet("/api/reports/billing-exceptions") });
  const uninvoiced = useQuery({ queryKey: ["/api/reports/completed-uninvoiced"], queryFn: () => reportGet("/api/reports/completed-uninvoiced") });
  const arInvoices = useQuery({ queryKey: ["/api/reports/ar-invoices", 90], queryFn: () => reportGet("/api/reports/ar-invoices?days=90") });

  const arRows: any[] = ar.data?.rows ?? [];
  const total = arRows.reduce((a, r) => a + r.grossBalance, 0);
  const uninvoicedRows: any[] = uninvoiced.data?.rows ?? [];
  const uninvoicedValue = uninvoicedRows.reduce((a, r) => a + r.serviceCents, 0);
  const ar90: any[] = arInvoices.data?.rows ?? [];

  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [uninvoiced.data, arInvoices.data]);

  return (
    <AdminReportGate>
    <ResponsiveLayout title="Billing" subtitle="Current invoice state — not a history of invoicing activity"
      breadcrumbs={[{ label: "Reports", href: "/reports" }, { label: "Billing" }]}
      backTo={{ label: "All reports", href: "/reports" }}
      isLoading={ar.isLoading}>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <b>Current AR only.</b> Dottie stores just the present payment state, so AR as of an earlier
          date cannot be reconstructed. Invoices are aged from the payment record’s created date, used
          as an invoice-date proxy. Balances are <b>gross</b> — service plus pass-through.
        </div>

        {ar.error && <p className="text-sm text-red-700 mt-4">{(ar.error as Error).message}</p>}

        <h2 className="text-xl font-medium mt-10 mb-1">Current AR aging</h2>
        <p className="text-sm text-gray-500 mb-4">{usd(total)} outstanding across {arRows.reduce((a, r) => a + r.invoices, 0)} invoices.</p>
        <CountBarChart label="Gross balance" color="#f59e0b"
          rows={arRows.map(r => ({ label: `${r.band} (${r.invoices} inv)`, value: r.grossBalance }))} />

        <h2 id="ar-90" className="text-xl font-medium mt-12 mb-1 scroll-mt-20">AR 90+ days</h2>
        <p className="text-sm text-gray-500 mb-4">
          Chaseable invoices aged from the payment record’s created date. Click a row to open the work order.
        </p>
        <ReportTable
          columns={[
            { key: "workOrderNumber", header: "Work order" },
            { key: "companyName", header: "Client" },
            { key: "status", header: "Status", render: (r: any) => formatStatus(r.status) },
            { key: "ageDays", header: "Age", align: "right", render: (r: any) => `${Math.round(r.ageDays)}d` },
            { key: "grossBalance", header: "Gross balance", align: "right", render: (r: any) => usd(r.grossBalance) },
          ]}
          rows={ar90}
          onRowClick={(r: any) => { setLocation(`/work-orders/${r.workOrderId}`); }}
          emptyMessage="No chaseable invoice is 90 days old."
        />
        {ar90.length > 0 && (
          <a href="/api/reports/ar-invoices.csv?days=90" className="text-sm text-indigo-600 hover:underline mt-3 inline-block">
            Download 90+ day invoices as CSV
          </a>
        )}

        <h2 id="uninvoiced" className="text-xl font-medium mt-12 mb-1 scroll-mt-20">Finished work never invoiced</h2>
        <p className="text-sm text-gray-500 mb-4 max-w-3xl">
          Work Complete, Closed, or legacy Completed, with no live (non-voided) invoice.
          This is the money-left-on-the-table list.
        </p>
        {uninvoicedRows.length > 0 && (
          <Finding>
            <b>{uninvoicedRows.length} orders · {money(uninvoicedValue)} service value</b> finished without a live invoice.
          </Finding>
        )}
        <ReportTable
          columns={[
            { key: "workOrderNumber", header: "Work order" },
            { key: "companyName", header: "Client" },
            { key: "status", header: "Status", render: (r: any) => formatStatus(r.status) },
            { key: "currentAssignee", header: "Owner", render: (r: any) => r.currentAssignee ?? "—" },
            { key: "ageDays", header: "Age", align: "right", render: (r: any) => `${Math.round(r.ageDays)}d` },
            { key: "serviceCents", header: "Service value", align: "right",
              render: (r: any) => money(r.serviceCents) },
          ]}
          rows={uninvoicedRows.slice(0, 50)}
          onRowClick={(r: any) => { setLocation(`/work-orders/${r.id}`); }}
          emptyMessage="No finished work is missing an invoice."
        />
        {uninvoicedRows.length > 0 && (
          <a href="/api/reports/completed-uninvoiced.csv" className="text-sm text-indigo-600 hover:underline mt-3 inline-block">
            Download uninvoiced CSV
          </a>
        )}

        <h2 className="text-xl font-medium mt-12 mb-4">Billing exceptions</h2>
        <ReportTable
          columns={[
            { key: "exception", header: "Exception",
              render: (r: any) => (<><div>{r.exception}</div><div className="text-xs text-gray-500">{r.detail}</div></>) },
            { key: "count", header: "Count", align: "right" },
            { key: "exposure", header: "Exposure", align: "right",
              render: (r: any) => r.exposure === null ? "—" : usd(r.exposure) },
          ]}
          rows={exceptions.data?.rows ?? []}
        />

        <div className="mt-6 flex gap-4">
          <a href="/api/reports/ar-aging.csv" className="text-sm text-indigo-600 hover:underline">AR aging CSV</a>
          <a href="/api/reports/billing-exceptions.csv" className="text-sm text-indigo-600 hover:underline">Exceptions CSV</a>
        </div>
        <Caveats items={[
          ...(ar.data?.meta?.caveats ?? []),
          ...(exceptions.data?.meta?.caveats ?? []),
        ].filter((c, i, a) => a.findIndex(x => x.code === c.code) === i)} />
    </ResponsiveLayout>
    </AdminReportGate>
  );
}
